<template>
  <d-container
    fluid
    class="main-content-container px-4">
    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4">
      <d-col
        col
        sm="6"
        class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">
          {{ $t('purchase.ordersFromSuppliers') }}
        </span>
        <h3 class="page-title">
          {{ $t('purchase.reservation.listOfReservationsFormOrdersWithSuppliers') }}
        </h3>
      </d-col>
    </d-row>

    <d-row
      style="width: 100%;"
      class="d-flex ml-0 mb-3 page-filters">
      <validation-observer
        ref="purchases-reservations-filters-form"
        tag="form"
        style="width: 100%;"
        class="d-flex">
        <d-input
          :placeholder="$t('order.enterOrderNumber')"
          v-model="filters.order" />

        <v-select
          class="ml-2 mr-2 w-50"
          :options="productOptions"
          v-model="filters.product_id"
          :placeholder="$t('product.selectProduct')"
          :searchable="true"
          label="text"
          style="min-width: 240px;"
          :reduce="product => product.value">
          <template slot="no-options">
            {{ $t('product.noProductsMatchingGivenPhrase') }}&hellip;
          </template>
        </v-select>

        <v-select
          class="ml-2 mr-2 w-50"
          :options="purchaseOptions"
          v-model="filters.purchase_id"
          :placeholder="$t('purchase.selectPurchase')"
          :searchable="true"
          label="text"
          style="min-width: 260px;"
          :reduce="purchase => purchase.value">
          <template slot="no-options">
            {{ $t('purchase.noPurchasesMatchingPhrase') }}&hellip;
          </template>
          <template slot="option" slot-scope="option">
            <div class="d-center">
              {{ option.text }} <template v-if="option.date">({{ option.date }})</template>
            </div>
          </template>
          <template slot="selected-option" slot-scope="option">
            <div class="selected d-center">
              {{ option.text }} <template v-if="option.date">({{ option.date }})</template>
            </div>
          </template>
        </v-select>

        <v-select
          class="ml-2 mr-2"
          :options="purchaseStatusOptions"
          v-model="filters.purchase_status"
          :placeholder="$t('purchase.purchaseStatus')"
          :searchable="false"
          label="text"
          style="min-width: 200px;"
          :clearable="false"
          :reduce="purchaseStatus => purchaseStatus.value">
        </v-select>

        <d-button
          theme="info"
          outline
          class="text-nowrap ml-2"
          @click.prevent="validateFiltersForm">
          {{ $t('ui.search') }}
        </d-button>

        <d-button
          theme="danger"
          outline
          class="text-nowrap ml-2"
          @click.prevent="clearFilters">
          {{ $t('ui.resetFilters') }}
        </d-button>
      </validation-observer>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0">{{ $t('product.product') }}</th>
            <th scope="col" class="border-0" style="width: 300px;">{{ $t('ui.order') }}</th>
            <th scope="col" class="border-0" style="width: 300px">{{ $t('purchase.purchase') }}</th>
            <th scope="col" class="border-0" style="width: 150px;">{{ $t('ui.amount') }}</th>
          </tr>
        </thead>
        <tbody v-if="!loadError">
          <tr
            v-for="(reservation, index) of reservations"
            :key="'reservation-' + index">
            <td class="align-middle">
              {{ reservation.product.name }}<br>
              <small>{{ $t('product.productCode') }}: <strong>{{ reservation.product.code }}</strong></small>
            </td>
            <td class="align-middle">
              <template v-if="reservation.order">
                <router-link :to="'/zamowienia/edycja/' + reservation.order.id + '/z-rezerwacji-zakupow'">
                  {{ reservation.order.order_name }}
                </router-link>
              </template>
            </td>
            <td class="align-middle">
              <router-link :to="'/zakupy/edycja/' + reservation.purchase_product.id + '/z-rezerwacji-zakupow'">
                {{ reservation.purchase_product.customer_name }} (#{{ reservation.purchase_product.id }})
              </router-link>
              <br>
              <small v-if="reservation.purchase_product.date">
                {{ $t('ui.deliveryDate') }}:
                <strong>
                  {{ reservation.purchase_product.date }}
                </strong>
              </small>
            </td>
            <td class="align-middle">
              {{ reservation.purchase_quantity }} {{ $t('ui.piecesAbbr') }}
            </td>
          </tr>
          <tr v-if="reservations.length === 0">
            <td
              colspan="8"
              class="text-center">
              {{ $t('purchase.reservation.noPurchasesReservationsToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="!allDataLoaded"
        class="pb-3 pt-3 text-center overlay-loading">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import HasFilters from './../mixins/HasFilters.vue';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import FormUtils from './../utils/FormUtils.js';
import Pagination from './../components/common/Pagination.vue';

export default {
  name: 'purchases-reservations',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  computed: {
    allDataLoaded () {
      return this.loaded && this.loadedPurchases && this.loadedProducts;
    },
    productOptions () {
      let options = [];

      for (let i = 0; i < this.products.length; i++) {
        let option = {
          text: this.products[i].code,
          value: this.products[i].id
        };

        options.push(option);
      }

      return options;
    },
    purchaseOptions () {
      let options = [];

      for (let i = 0; i < this.purchases.length; i++) {
        let option = {
          text: this.purchases[i].customer_name,
          value: this.purchases[i].id,
          date: this.purchases[i].date
        };

        options.push(option);
      }

      return options;
    },
    purchaseStatusOptions () {
      return [
        {
          text: this.$t('purchase.new'),
          value: 'NEW'
        },
        {
          text: this.$t('purchase.awaitingPayment'),
          value: 'WAITING_PAYMENT'
        },
        {
          text: this.$t('purchase.awaitingProducts'),
          value: 'WAITING_PRODUCTS'
        },
        {
          text: this.$t('purchase.finished'),
          value: 'CLOSED'
        }
      ];
    }
  },
  data() {
    return {
      reservations: [],
      loaded: false,
      loadedPurchases: false,
      loadedProducts: false,
      loadError: false,
      purchases: [],
      products: [],
      filters: {
        order: '',
        purchase_id: null,
        purchase_status: 'NEW',
        product_id: null
      }
    };
  },
  mounted () {
    this.loadFilteredData();
    this.loadAdditionalData();
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = {};

      if (this.filters.order) {
        where.order = this.filters.order;
      }

      if (this.filters.product_id) {
        where.product_id = this.filters.product_id;
      }

      if (this.filters.purchase_id) {
        where.purchase_id = this.filters.purchase_id;
      }

      if (this.filters.purchase_status) {
        where.purchase_status = this.filters.purchase_status;
      }

      ListUtils.loadPaginatedItemsData(this, {
        endpoint: '/purchases/reservations?page=' + this.currentPage,
        listField: 'reservations',
        params: {
          where
        }
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/products/all-items',
        loadedKey: 'loadedProducts',
        outputKey: 'products',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/purchases/items?return=get&where[status][]=NEW&where[status][]=WAITING_PAYMENTS&where[status][]=WAITING_PRODUCTS',
        loadedKey: 'loadedPurchases',
        outputKey: 'purchases',
        errorKey: 'loadError'
      });
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        order: '',
        purchase_id: null,
        purchase_status: 'NEW',
        product_id: null
      };

      this.$bus.$emit('view-filters-reset');
      this.$bus.$emit('pagination-reset', false);
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['purchases-reservations-filters-form'], this.debouncedLoadFilteredData);
    }
  }
};
</script>
